import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, CreditCard, CheckCircle, ArrowRight, LogOut } from 'lucide-react';
import Logo from '../components/Logo';
import { toast } from 'sonner';

export default function CheckoutResumePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stripe/billing`)
      .then(r => { if (r.data.subscription_status === 'active') navigate('/'); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate]);

  const handleCompletePayment = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/stripe/resume-checkout`);
      if (res.data.already_active) { navigate('/'); return; }
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed. Please try again.');
      setLoading(false);
    }
  };

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0F1E' }}>
      <Loader2 size={32} className="text-blue-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0F1E' }}>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={38} showText light /></div>
        <div className="rounded-2xl overflow-hidden"
          style={{ background: '#0D1526', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
          <div className="px-8 py-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <CreditCard size={18} className="text-red-400" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-white text-lg">Payment Required</h2>
                <p className="text-xs" style={{ color: 'rgba(148,163,184,0.6)' }}>Complete your subscription to access the dashboard</p>
              </div>
            </div>
          </div>
          <div className="px-8 py-6 space-y-5">
            <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-sm font-medium text-white mb-1">Hi {user?.full_name || user?.username} 👋</p>
              <p className="text-sm" style={{ color: 'rgba(148,163,184,0.75)' }}>Your account was created but payment wasn’t completed. Click below to finish your subscription.</p>
            </div>
            {['Full Arch ROI dashboard with real-time KPIs','Multi-location performance tracking','Marketing ROI calculator & funnel analytics','Shareable staff forms & audit log'].map(item => (
              <div key={item} className="flex items-center gap-2.5">
                <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                <span className="text-sm" style={{ color: 'rgba(203,213,225,0.8)' }}>{item}</span>
              </div>
            ))}
            <div className="flex items-baseline gap-1 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.25)' }}>
              <span className="text-2xl font-bold text-white font-heading">$249</span>
              <span className="text-sm" style={{ color: 'rgba(148,163,184,0.6)' }}>/month · First location included · +$29/mo per extra</span>
            </div>
            <Button onClick={handleCompletePayment} disabled={loading} className="w-full h-12 gap-2 font-bold text-base"
              style={{ background: 'linear-gradient(135deg,#2563EB,#1D4ED8)', color: 'white', boxShadow: '0 4px 20px rgba(37,99,235,0.35)' }}>
              {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : <><ArrowRight size={16} /> Complete Payment — $249/mo</>}
            </Button>
            <p className="text-center text-xs" style={{ color: 'rgba(100,116,139,0.5)' }}>Secure checkout via Stripe · Cancel anytime</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate('/login'); }}
          className="flex items-center gap-1.5 mx-auto mt-5 text-xs hover:text-white transition-colors"
          style={{ color: 'rgba(100,116,139,0.5)' }}>
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </div>
  );
}
